import React, { useCallback, useState } from 'react';
import { useAuth } from '@/auth/AuthContext';
import { apiURL, useToken } from '@/helpers/apiURL';
import styled from 'styled-components';
import { Modal, Dropdown } from '@stats/playbook-components';
import { TextArea } from '@/components/TextArea';
import ReactDOM from 'react-dom';
import EventBus from '@/helpers/eventBus';

const StyledModal = styled(Modal)`
  && {
    width: 400px;
    margin-top: 0px;
    height: auto;
    min-height: 400px;
    border-top: 4px solid red;
  }
`;

const StyledTextArea = styled(TextArea)`
  && {
    textarea {
      resize: none;
      width: 382px;
      min-height: 150px;
    }
  }
`;

const StyledDropdown = styled(Dropdown)`
  && {
    z-index: 9;
    margin-bottom: 15px;
  }
`;

type EditNoteModalContentProps = {
  note: { text: string; sk: string; deliveryName?: string };
  disabledCallback: Function;
  setModalNote: Function;
  setDeliveryNameValue: Function;
  deliveries: Array<any>;
};

const EditNoteModalContent: React.FC<EditNoteModalContentProps> = (props) => {
  const {
    note,
    setModalNote,
    setDeliveryNameValue,
    disabledCallback,
    deliveries,
  } = props;

  const inputOnChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    disabledCallback(e.currentTarget.value === '');
    setModalNote(e.currentTarget.value);
  }, []);

  const menuOnItemSelect = useCallback((e: any) => {
    disabledCallback(e.value === '');
    setDeliveryNameValue(e);
  }, []);

  return (
    <>
      <StyledDropdown
        label="Delivery"
        value={note.deliveryName}
        menuItems={deliveries?.map((delivery) => {
          return { value: delivery.name };
        })}
        onItemSelect={menuOnItemSelect}
      />
      <StyledTextArea
        label="Enter Note"
        value={note.text}
        onChange={inputOnChange}
        required
      />
    </>
  );
};

type EditNoteModalProps = {
  handleClose: () => void;
  note: { text: string; sk: string; deliveryName: string };
  clientId: string;
  deliveries: Array<any>;
};

const EditNoteModal: React.FC<EditNoteModalProps> = (props) => {
  const { handleClose, note, clientId, deliveries } = props;
  const [disabled, setDisabled] = useState<boolean>(true);
  const [modalNote, setModalNote] = useState<string>(note.text);
  const [deliveryName, setDeliveryName] = useState<string>(note.deliveryName);
  const { user } = useAuth();
  const { privateFetch } = useToken();

  const setDeliveryNameValue = useCallback((value: string) => {
    setDeliveryName(value);
  }, []);

  const editNote = useCallback(async () => {
    const url = `${apiURL}clients/${clientId}/notes/${note.sk}`;
    let putBody = {
      // @ts-ignore
      user: user.name,
      text: modalNote,
      deliveryName: deliveryName,
    };

    try {
      const settings = {
        method: 'PUT',
        body: JSON.stringify(putBody),
      };

      await privateFetch(url, settings);
      handleClose();
    } catch (e) {
      EventBus.dispatch('notify', { variant: 'error', message: 'Failed to update note' });
      return e;
    }
  }, [apiURL, clientId, note, user, modalNote, deliveryName, handleClose]);

  return (
    ReactDOM.createPortal(
      <StyledModal
        title="Edit Note"
        handleClose={handleClose}
        modalAction={{
          text: 'Edit',
          onClick: editNote,
          disabled: disabled,
        }}
      >
        <EditNoteModalContent
          note={note}
          setModalNote={setModalNote}
          setDeliveryNameValue={setDeliveryNameValue}
          disabledCallback={setDisabled}
          deliveries={deliveries}
        />
      </StyledModal>,
      document.body
    )
  );
};

export default EditNoteModal;
